import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Artistic%20overhead%20flat%20lay%20of%20curated%20fashion%20items%20including%20folded%20cashmere%20sweater%2C%20leather%20accessories%2C%20designer%20sunglasses%2C%20and%20fresh%20eucalyptus%20branches%20on%20a%20warm%20terracotta%20linen%20backdrop%2C%20soft%20morning%20sunlight%20casting%20delicate%20shadows%2C%20Mediterranean%20villa%20aesthetic%2C%20editorial%20luxury%20catalog%20photography&width=1600&height=900&seq=hero-bg-unique-fashion&orientation=landscape"
          alt="Hipobuy Spreadsheet - Mode et style"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-4 md:px-6 pt-24 pb-12">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-20">
          {/* Left content */}
          <div className="lg:w-3/5 w-full">
            {/* Category dropdown trigger */}
            <div className="relative inline-block mb-8" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-background-50/90 backdrop-blur-sm border border-background-200/60 text-foreground-800 text-sm font-medium hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                <span>Explorer les catégories</span>
                <i
                  className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {dropdownOpen && (
                <div className="absolute left-0 mt-2 w-72 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <i className="ri-arrow-right-up-line w-4 h-4 inline-flex items-center justify-center mr-2 text-foreground-400"></i>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Hero heading */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading text-background-50 leading-tight mb-6 max-w-2xl">
              Découvrez <span className="text-primary-300">Hipobuy Spreadsheet</span> – L&apos;outil nouvelle génération
            </h1>
          </div>

          {/* Right content */}
          <div className="lg:w-2/5 w-full flex flex-col justify-end">
            <p className="text-background-200/90 text-base md:text-lg leading-relaxed mb-8 max-w-md">
              Optimisez vos données, automatisez vos workflows et comparez les meilleures solutions spreadsheet en un seul endroit.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Hipobuy Spreadsheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 border-background-50/70 text-background-50 text-sm font-semibold hover:bg-background-50 hover:text-foreground-900 transition-all cursor-pointer whitespace-nowrap"
              >
                Acheter maintenant
                <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom brand image */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pb-8 md:pb-12">
        <div className="w-full h-48 md:h-64 rounded-2xl overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20boutique%20storefront%20display%20with%20mannequins%20showcasing%20autumn%20fashion%20collection%2C%20warm%20golden%20interior%20lighting%20spilling%20onto%20cobblestone%20street%2C%20Parisian%20shopping%20district%20atmosphere%2C%20luxury%20retail%20aesthetic%2C%20cream%20and%20honey%20toned%20composition%2C%20sophisticated%20editorial%20photography&width=1400&height=500&seq=hero-btm-unique-shop&orientation=landscape"
            alt="Collection Hipobuy Spreadsheet"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}